/**
 * Normalisation timesheet CRA : dates, déduplication, filtrage par type mission.
 * Source unique partagée SPA (alias Vite) et API Next (draft / submit).
 */

import { filterTimesheetsForBilling } from './financialCalculations'

/** Date calendaire YYYY-MM-DD (sans conversion timezone sur le jour). */
export function normalizeTimesheetDateKey(date) {
  if (date === undefined || date === null || date === '') return ''
  if (typeof date === 'string') {
    let dateKey = date
    if (dateKey.includes('T')) dateKey = dateKey.split('T')[0]
    if (dateKey.length > 10) dateKey = dateKey.substring(0, 10)
    return dateKey
  }
  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    return date.toISOString().split('T')[0]
  }
  return String(date).slice(0, 10)
}

function resolveTimesheetEntryType(entry) {
  return String(entry?.type || entry?.entryType || '').toLowerCase()
}

function resolveTimesheetMissionId(entry) {
  const missionId = entry?.mission_id ?? entry?.missionId
  return missionId != null && missionId !== '' ? String(missionId) : ''
}

function getDeliverableIdFromEntry(entry) {
  if (!entry || typeof entry !== 'object') return null
  const top = entry.deliverableId ?? entry.deliverable_id
  if (top != null && top !== '') return String(top)
  const description = entry.description
  if (!description || typeof description !== 'string') return null
  try {
    const parsed = JSON.parse(description)
    const rawDid = parsed.deliverableId ?? parsed.deliverable_id
    if (rawDid != null && rawDid !== '') return String(rawDid)
  } catch {
    /* not JSON */
  }
  return null
}

/**
 * Déduplique les lignes de feuille de temps :
 * - livrables : une ligne par deliverable_id ;
 * - daily/hourly : une ligne par mission et date normalisée
 *   (deux missions d'un même CRA peuvent déclarer le même jour) ;
 * - autres lignes : conservées telles quelles.
 */
export function deduplicateTimesheets(timesheets) {
  if (!Array.isArray(timesheets)) return []

  const dateBasedByKey = new Map()
  const dateBasedOrder = []
  const unique = []
  const seenDeliverableIds = new Set()

  timesheets.forEach((entry) => {
    const did = getDeliverableIdFromEntry(entry)
    if (did) {
      if (!seenDeliverableIds.has(did)) {
        unique.push(entry)
        seenDeliverableIds.add(did)
      }
      return
    }

    const entryType = resolveTimesheetEntryType(entry)
    if ((entryType === 'daily' || entryType === 'hourly') && entry.date) {
      const normalizedDate = normalizeTimesheetDateKey(entry.date)
      if (!normalizedDate) return
      const dedupeKey = `${resolveTimesheetMissionId(entry)}|${normalizedDate}`
      if (!dateBasedByKey.has(dedupeKey)) {
        dateBasedOrder.push(dedupeKey)
      }
      dateBasedByKey.set(dedupeKey, { ...entry, date: normalizedDate })
      return
    }

    unique.push(entry)
  })

  dateBasedOrder.forEach((dateKey) => {
    const row = dateBasedByKey.get(dateKey)
    if (row) unique.push(row)
  })

  return unique
}

/**
 * Pipeline unique : déduplication + filtrage par modèle de facturation mission.
 * À appeler au chargement du brouillon et avant persistance (save / submit).
 */
export function normalizeTimesheetsForMission(timesheets, missionType) {
  return filterTimesheetsForBilling(deduplicateTimesheets(timesheets), missionType)
}
